import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
    GraduationCap, Clock, ClipboardList, Calendar, Award,
    Users, CheckCircle, ArrowRight, FileText, Plus
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TeacherDashboard = () => {
    const [stats, setStats] = useState(null);
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [statsRes, leaveRes] = await Promise.all([
                    axios.get(`${API_URL}/api/dashboard/stats`, { withCredentials: true }),
                    axios.get(`${API_URL}/api/leave`, { withCredentials: true })
                ]);
                setStats(statsRes.data);
                setLeaveRequests(leaveRes.data.slice(0, 3));
            } catch (error) {
                if (process.env.NODE_ENV === 'development') console.error('Failed to fetch data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    if (loading) {
        return (
            <DashboardLayout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]"></div>
                </div>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="teacher-dashboard">
                {/* Welcome Section */}
                <div className="bg-gradient-to-r from-[#064E3B] to-[#047857] rounded-xl p-6 text-white">
                    <h1 className="text-2xl font-bold font-['Outfit'] mb-2">Teacher Dashboard</h1>
                    <p className="text-white/80">Manage your classes and track student progress</p>
                </div>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card className="card-surface">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-[#4B5563]">My Classes</p>
                                    <p className="text-3xl font-bold text-[#1F2937] tabular-nums">{stats?.my_classes || 0}</p>
                                </div>
                                <div className="p-3 rounded-xl bg-[#064E3B]/10">
                                    <Users className="h-6 w-6 text-[#064E3B]" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="card-surface">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-[#4B5563]">My Students</p>
                                    <p className="text-3xl font-bold text-[#1F2937] tabular-nums">{stats?.my_students || 0}</p>
                                </div>
                                <div className="p-3 rounded-xl bg-[#D97706]/10">
                                    <GraduationCap className="h-6 w-6 text-[#D97706]" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="card-surface">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-[#4B5563]">Pending Homework</p>
                                    <p className="text-3xl font-bold text-[#1F2937] tabular-nums">{stats?.pending_homework || 0}</p>
                                </div>
                                <div className="p-3 rounded-xl bg-[#10B981]/10">
                                    <ClipboardList className="h-6 w-6 text-[#10B981]" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="card-surface">
                        <CardContent className="p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-[#4B5563]">Today</p>
                                    <p className="text-lg font-bold text-[#1F2937]">
                                        {new Date().toLocaleDateString('en-IE', { weekday: 'short', month: 'short', day: 'numeric' })}
                                    </p>
                                </div>
                                <div className="p-3 rounded-xl bg-[#F59E0B]/10">
                                    <Calendar className="h-6 w-6 text-[#F59E0B]" />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Quick Actions & Leave Requests */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Quick Actions */}
                    <Card className="card-surface">
                        <CardHeader>
                            <CardTitle className="font-['Outfit']">Quick Actions</CardTitle>
                            <CardDescription>Common teacher tasks</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-3">
                            <Link to="/teacher/attendance">
                                <Button variant="outline" className="w-full justify-start" data-testid="quick-attendance">
                                    <CheckCircle className="h-4 w-4 mr-2" />
                                    Record Attendance
                                </Button>
                            </Link>
                            <Link to="/teacher/homework">
                                <Button variant="outline" className="w-full justify-start" data-testid="quick-homework">
                                    <ClipboardList className="h-4 w-4 mr-2" />
                                    Assign Homework
                                </Button>
                            </Link>
                            <Link to="/teacher/exams">
                                <Button variant="outline" className="w-full justify-start" data-testid="quick-exams">
                                    <FileText className="h-4 w-4 mr-2" />
                                    Manage Exams
                                </Button>
                            </Link>
                            <Link to="/teacher/student-of-month">
                                <Button variant="outline" className="w-full justify-start" data-testid="quick-student-month">
                                    <Award className="h-4 w-4 mr-2" />
                                    Student of the Month
                                </Button>
                            </Link>
                        </CardContent>
                    </Card>

                    {/* Leave Requests */}
                    <Card className="card-surface">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <div>
                                <CardTitle className="font-['Outfit']">My Leave Requests</CardTitle>
                                <CardDescription>Recent leave applications</CardDescription>
                            </div>
                            <Link to="/teacher/leave">
                                <Button variant="ghost" size="sm">
                                    View All <ArrowRight className="h-4 w-4 ml-1" />
                                </Button>
                            </Link>
                        </CardHeader>
                        <CardContent>
                            {leaveRequests.length > 0 ? (
                                <div className="space-y-3">
                                    {leaveRequests.map((leave) => (
                                        <div key={leave.id} className="flex items-center justify-between p-3 bg-[#FAF9F6] rounded-lg">
                                            <div>
                                                <p className="text-sm font-medium text-[#1F2937]">
                                                    {leave.start_date} - {leave.end_date}
                                                </p>
                                                <p className="text-xs text-[#4B5563] line-clamp-1">{leave.reason}</p>
                                            </div>
                                            <Badge
                                                variant={leave.status === 'approved' ? 'default' : leave.status === 'rejected' ? 'destructive' : 'secondary'}
                                                className={leave.status === 'approved' ? 'bg-[#10B981]' : ''}
                                            >
                                                {leave.status}
                                            </Badge>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center py-8">
                                    <Clock className="h-12 w-12 text-[#4B5563]/30 mb-4" />
                                    <p className="text-[#4B5563] mb-4">No leave requests</p>
                                    <Link to="/teacher/leave">
                                        <Button className="btn-primary" data-testid="request-leave-button">
                                            <Plus className="h-4 w-4 mr-2" />
                                            Request Leave
                                        </Button>
                                    </Link>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default TeacherDashboard;
